import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Search,
  Cog,
  MoreHorizontal,
  Pencil,
  Archive,
  RotateCcw,
  Settings2,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { listSimulators, deleteSimulator, restoreSimulator, permanentlyDeleteSimulator } from "@/lib/api/simulators";
import { formatDate } from "@/lib/utils";
import { getErrorMessage } from "@/lib/api/client";
import { AdminGuard } from "@/lib/auth/AdminGuard";
import type { Simulator, SimulatorStatus } from "@/types";
import { CreateSimulatorDialog } from "@/components/simulators/CreateSimulatorDialog";

export const Route = createFileRoute('/_dashboard/admin/simulators/')({
  component: AdminSimulatorsPage,
})

function AdminSimulatorsPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<SimulatorStatus | "all">("all");
  const [page, setPage] = useState(1);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSimulator, setSelectedSimulator] = useState<Simulator | null>(null);

  const queryClient = useQueryClient();
  const pageSize = 10;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-simulators", { page, page_size: pageSize, search, status: status === "all" ? undefined : status }],
    queryFn: () =>
      listSimulators({
        page,
        page_size: pageSize,
        search: search || undefined,
        status: status === "all" ? undefined : status,
        include_unpublished: true,
      }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) => deleteSimulator(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-simulators"] });
      toast.success("Simulateur archivé");
      setArchiveDialogOpen(false);
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (id: number) => restoreSimulator(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-simulators"] });
      toast.success("Simulateur restauré");
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => permanentlyDeleteSimulator(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-simulators"] });
      toast.success("Simulateur supprimé définitivement");
      setDeleteDialogOpen(false);
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  const handleArchive = (simulator: Simulator) => {
    setSelectedSimulator(simulator);
    setArchiveDialogOpen(true);
  };

  const handleDelete = (simulator: Simulator) => {
    setSelectedSimulator(simulator);
    setDeleteDialogOpen(true);
  };

  return (
    <AdminGuard>
    <div className="flex flex-col h-full">
      <Header title="Configuration simulateurs" />

      <div className="flex-1 p-6 space-y-6 overflow-auto">
        {/* Actions bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-4 w-full sm:w-auto">
            <div className="relative flex-1 sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un simulateur..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>
            <Select
              value={status}
              onValueChange={(value) => {
                setStatus(value as SimulatorStatus | "all");
                setPage(1);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="archived">Archivés</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nouveau simulateur
          </Button>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Mis à jour</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-10 w-10 rounded-lg" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-48" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                    </TableRow>
                  ))
                ) : data?.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-32 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Cog className="h-10 w-10 opacity-50" />
                        <p>Aucun simulateur configuré</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCreateDialogOpen(true)}
                        >
                          Créer un simulateur
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.items.map((simulator, index) => (
                    <TableRow
                      key={simulator.id}
                      className="border-b transition-colors hover:bg-muted/50"
                      style={{
                        animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both`,
                      }}
                    >
                      <TableCell>
                        <Link
                          to="/admin/simulators/$id"
                          params={{ id: String(simulator.id) }}
                          className="flex items-center gap-3 hover:text-primary transition-colors"
                        >
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Settings2 className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{simulator.name}</p>
                            {simulator.description && (
                              <p className="text-sm text-muted-foreground line-clamp-1 max-w-xs">
                                {simulator.description}
                              </p>
                            )}
                          </div>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <code className="px-2 py-1 bg-muted rounded text-xs">
                          {simulator.slug}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            simulator.status === "active" ? "success" : "secondary"
                          }
                        >
                          {simulator.status === "active" ? "Actif" : "Archivé"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(simulator.updated_at)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to="/admin/simulators/$id" params={{ id: String(simulator.id) }}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Configurer
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {simulator.status === "active" ? (
                              <DropdownMenuItem
                                onClick={() => handleArchive(simulator)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Archive className="mr-2 h-4 w-4" />
                                Archiver
                              </DropdownMenuItem>
                            ) : (
                              <>
                                <DropdownMenuItem
                                  onClick={() => restoreMutation.mutate(simulator.id)}
                                >
                                  <RotateCcw className="mr-2 h-4 w-4" />
                                  Restaurer
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDelete(simulator)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Supprimer définitivement
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {data.total_items} simulateur{data.total_items > 1 ? "s" : ""} au total
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Précédent
              </Button>
              <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded text-sm">
                {page} / {data.total_pages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
                disabled={page === data.total_pages}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <CreateSimulatorDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archiver le simulateur</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir archiver &quot;{selectedSimulator?.name}&quot; ?
              Cette action peut être annulée.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setArchiveDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedSimulator && archiveMutation.mutate(selectedSimulator.id)}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? "Archivage..." : "Archiver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer définitivement le simulateur</DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Le simulateur &quot;{selectedSimulator?.name}&quot; et toutes ses données associées (versions, variables, champs, etc.) seront définitivement supprimés.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedSimulator && deleteMutation.mutate(selectedSimulator.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Suppression..." : "Supprimer définitivement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </AdminGuard>
  );
}

